/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';
import { Fragment, useEffect, useRef, useState } from 'react';

import { EMPTY_STRING } from '../../const';
import ageQuestions from '../../mocks/data/questions/age';
import filterMenuStyle from '../FiltersMenu/styles';
import { ErrorIcon } from '../Icons';
import useStyles from './styles';

const FilterByAge = ({
  onSave,
  currentValue: value,
  currentSchoolYearValue,
  onErrorChange,
  filterShowHide,
}) => {
  const styles = useStyles();
  const filterMenuStyles = filterMenuStyle();
  const [selectedSchoolYear, setSelectedSchoolYear] = useState(
    currentSchoolYearValue
  );

  const [currentValue, setCurrentValue] = useState(
    Number(value) === 0 ? '' : value
  );

  const lastSaved = useRef({
    Age: value || '',
    SchoolYear: '',
  });
  const hasError = currentValue !== '' && currentValue < 5;
  // To track if the user is using a keyboard
  const [isKeyboard, setIsKeyboard] = useState(false);
  // Handle input change
  const handleChange = e => {
    const val = e.target.value;
    if (val === '0' || Number(val) === 0) {
      setCurrentValue('');
    } else {
      setCurrentValue(val);
    }
  };
  const handleSchoolYearChange = option => {
    setSelectedSchoolYear(prev => (prev === option ? '' : option));
  };
  const [{ yearGroup }] = ageQuestions;
  const selectedYear = yearGroup
    ?.filter(item => item.age === parseInt(currentValue, 10))[0]
    ?.options?.filter(option => option !== 'Other year group');
  // Call onSave only after currentValue updates
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const today = new Date();
      const currentyear = today.getFullYear();
      //sep 1st to dec 31st older year group
      const olderstartdate = new Date(`09/01/${currentyear}`);
      const olderenddate = new Date(`12/31/${currentyear}`);
      //jan 1st to aug 31st younger year group
      const youngerstartdate = new Date(`01/01/${currentyear}`);
      const youngerenddate = new Date(`08/31/${currentyear}`);
      let schoolYear = selectedSchoolYear;

      if (selectedSchoolYear === 'Other year group') {
        if (today >= olderstartdate && today <= olderenddate) {
          schoolYear = selectedYear[1];
        } else if (today >= youngerstartdate && today <= youngerenddate) {
          schoolYear = selectedYear[0];
        } else {
          schoolYear = EMPTY_STRING;
        }
      }

      const payload = {
        Age: currentValue,
        SchoolYear: schoolYear,
      };
      if (
        payload.Age !== lastSaved.current.Age ||
        payload.SchoolYear !== lastSaved.current.SchoolYear
      ) {
        onSave(payload);
        lastSaved.current = payload;
      }
    }, 500); // 500ms debounce delay

    return () => clearTimeout(delayDebounce); // cleanup on value change
  }, [currentValue, selectedSchoolYear, selectedYear, onSave]);

  useEffect(() => {
    if (selectedSchoolYear && !selectedYear?.includes(selectedSchoolYear)) {
      setSelectedSchoolYear('');
    }
  }, [selectedYear, selectedSchoolYear]);
  useEffect(() => {
    onErrorChange(hasError); // Notify parent whenever error state changes
  }, [hasError, onErrorChange]);

  // for clear filter Sync local state with prop changes
  useEffect(() => {
    setCurrentValue(Number(value) === 0 ? '' : value);
  }, [value]);

  const inputRef = useRef(null);

  useEffect(() => {
    // Indicating keyboard interaction.
    const handleKeyDown = e => {
      if (e.key === 'Tab') {
        setIsKeyboard(true);
      }
    };

    // when user used a pointer (mouse/touch)
    const handlePointerDown = () => {
      setIsKeyboard(false);
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('pointerdown', handlePointerDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, []);

  return (
    <Fragment>
      <div css={filterMenuStyles.filterHead}>
        <h5 css={filterMenuStyles.filterTitle}>Age</h5>
      </div>
      <div css={filterMenuStyles.filterBody}>
        <div css={styles.searchAgeFilter}>
          <input
            ref={inputRef}
            css={styles.inputField(hasError)}
            className={`${hasError ? 'error' : ''} ${
              isKeyboard ? 'keyboard-focus' : ''
            }`}
            type="text"
            placeholder="Enter age here"
            value={currentValue}
            onChange={handleChange}
            minLength="1"
            maxLength="2"
            tabIndex={filterShowHide ? 0 : -1}
            onKeyPress={event => {
              if (!/[0-9]/.test(event.key)) {
                event.preventDefault();
              }
            }}
          />
          {hasError && (
            <div css={styles.errorMessage}>
              <ErrorIcon /> Please enter an age between 5 and 99
            </div>
          )}
        </div>
        {selectedYear && selectedYear?.length > 0 && (
          <ul css={styles.listWrapper}>
            {selectedYear.map(option => (
              <li>
                <label
                  key={option}
                  htmlFor={option}
                  css={styles.chooseFilter(selectedSchoolYear === option)}
                >
                  <span>{option}</span>
                  <input
                    type="checkbox"
                    id={option}
                    name="schoolYear"
                    checked={selectedSchoolYear === option}
                    value={option}
                    css={styles.optionCheckbox}
                    onChange={() => handleSchoolYearChange(option)}
                    tabIndex={filterShowHide ? 0 : -1}
                  />
                  <span /> {/* This is the visual checkbox controlled by CSS */}
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Fragment>
  );
};

FilterByAge.propTypes = {
  currentValue: PropTypes.any,
  onSave: PropTypes.func,
};

export default FilterByAge;
